import React from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Users, Car, Wrench, PawPrint, Building2, Search, ChevronRight, Zap } from 'lucide-react';

const PET_TYPES = ['dog', 'cat', 'bird', 'rabbit', 'fish', 'hamster', 'turtle', 'other'];

export function FamilyModal({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /> Add Family Member</DialogTitle>
          <DialogDescription>Add up to 5 family members</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input data-testid="family-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
          </div>
          <div className="space-y-2">
            <Label>Phone *</Label>
            <Input data-testid="family-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91-XXXXXXXXXX" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="save-family-button" onClick={onSubmit} className="flex-1 bg-blue-600 hover:bg-blue-700">Save</Button>
            <Button onClick={() => onOpenChange(false)} variant="outline">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PetModal({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PawPrint className="w-5 h-5 text-orange-600" /> Add Pet</DialogTitle>
          <DialogDescription>Vaccination status will be verified by management</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="space-y-2">
            <Label>Pet Type *</Label>
            <Select value={form.pet_type} onValueChange={(v) => setForm({ ...form, pet_type: v })}>
              <SelectTrigger data-testid="pet-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PET_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input data-testid="pet-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Pet's name" />
          </div>
          <div className="space-y-2">
            <Label>Breed</Label>
            <Input data-testid="pet-breed-input" value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} placeholder="e.g., Golden Retriever" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="save-pet-button" onClick={onSubmit} className="flex-1 bg-orange-600 hover:bg-orange-700">Add Pet</Button>
            <Button onClick={() => onOpenChange(false)} variant="outline">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DailyHelpModal({ open, onOpenChange, staffSearch, onSearch, staffResults, onAdd }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench className="w-5 h-5 text-gk-teal" /> Add Daily Help</DialogTitle>
          <DialogDescription>Search from community staff</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input data-testid="staff-search-input" value={staffSearch} onChange={(e) => onSearch(e.target.value)} placeholder="Search staff..." className="pl-9" />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {staffResults.length === 0 && staffSearch.length > 0 && <p className="text-sm text-slate-500 text-center py-3">No staff found</p>}
            {staffResults.map((s) => (
              <div key={s.id} data-testid={`staff-result-${s.id}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-gk-bg cursor-pointer transition-colors" onClick={() => onAdd(s.id)}>
                <div><p className="font-semibold text-sm">{s.name}</p><p className="text-xs text-slate-500">{s.job_description} {s.phone && `| ${s.phone}`}</p></div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function VehicleModal({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Car className="w-5 h-5 text-emerald-600" /> Add Vehicle</DialogTitle>
          <DialogDescription>Limits per villa: 3 four-wheelers, 1 two-wheeler</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="space-y-2">
            <Label>Vehicle Type *</Label>
            <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
              <SelectTrigger data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="4-wheeler">4-Wheeler (Car/SUV)</SelectItem>
                <SelectItem value="2-wheeler">2-Wheeler (Bike/Scooter)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Make *</Label><Input data-testid="vehicle-make-input" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} placeholder="e.g., Toyota" /></div>
            <div className="space-y-2"><Label>Model *</Label><Input data-testid="vehicle-model-input" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="e.g., Camry" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Color *</Label><Input data-testid="vehicle-color-input" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="e.g., White" /></div>
            <div className="space-y-2"><Label>License Plate *</Label><Input data-testid="vehicle-plate-input" value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} placeholder="e.g., KA01AB1234" /></div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <input data-testid="vehicle-ev-checkbox" type="checkbox" checked={form.is_ev} onChange={(e) => setForm({ ...form, is_ev: e.target.checked })} className="w-4 h-4 text-green-600 rounded" id="ev-check" />
            <label htmlFor="ev-check" className="flex items-center gap-2 text-sm cursor-pointer"><Zap className="w-4 h-4 text-green-500" />Electric Vehicle (EV)</label>
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="save-vehicle-button" onClick={onSubmit} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Add Vehicle</Button>
            <Button onClick={() => onOpenChange(false)} variant="outline">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CommunityModal({ open, onOpenChange, search, onSearch, results, onJoin }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-gk-teal" /> Join a Community</DialogTitle>
          <DialogDescription>Search communities and request to join. Management will approve your request.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input data-testid="community-search-input" value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search community name..." className="pl-9" />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {results.length === 0 && search.length > 0 && <p className="text-sm text-slate-500 text-center py-3">No communities found</p>}
            {results.map((c) => (
              <div key={c.id} data-testid={`community-result-${c.id}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-gk-bg transition-colors">
                <div><p className="font-semibold text-sm">{c.name}</p><p className="text-xs text-slate-500">{c.address}</p></div>
                <Button size="sm" onClick={() => onJoin(c.id)} className="bg-gk-teal hover:bg-gk-teal-hover">Request</Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
